import React, {
  RefObject,
  useMemo,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import {
  RoomSettingDTO,
} from 'dtos';
import {
  useFormContext,
} from 'react-hook-form';
import {
  isEmptyString,
  notEmptyString,
} from '@populateui/utils';
import Input from 'pages/Dashboard/components/Input';
import {
  useConfirmDialog,
  useNotify,
} from 'core/hooks';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from 'core/icons/delete';
import Editable, {
  EditableInstance,
} from 'core/components/Editable';
import Tooltip from 'core/components/Tooltip';
import Controller from 'core/components/FormController';
import {
  lightenHexColor,
} from 'utils/colors';

type Props = {
  value: RoomSettingDTO;
  index: number;
  update: (index: number, value: RoomSettingDTO) => void;
  remove: (index: number, value: RoomSettingDTO) => void;
  color?: string;
};

export default function Room({
  value,
  index,
  update,
  remove,
  color,
}: Props) {
  const { confirm, closeConfirm } = useConfirmDialog();
  const [inProgress, setInProgress] = useState<boolean>(false);
  const notify = useNotify();
  const { roomSettingId, name } = value;
  const roomNameControlRef = useRef<HTMLInputElement>(null);
  const editableRef = useRef<EditableInstance>(null);
  const { control, watch, resetField } = useFormContext();
  const currentName = watch(`rooms.${index}.name`);
  const roomColor = useMemo(() => lightenHexColor(color ?? '', 50), [color]);

  const handleDeleteRoom = () => {
    if (!isNil(value.roomSettingId)) {
      confirm({
        content: (
          <div className="gap-2 flex flex-col">
            <p>
              Are you sure you want to delete room
              <br />
              <strong className="font-semibold">{name}</strong>
              ?
            </p>
            <p className="italic">
              All the existing appointments in this room will be marked as
              {' '}
              <strong className="font-semibold">Ready.</strong>
              <br />
            </p>
          </div>
        ),
        onConfirm: async () => {
          await remove(index, value);
          notify({
            message: 'Room has been deleted.',
            variant: 'success',
          });
          closeConfirm();
        },
        title: 'Delete Room',
        confirmText: 'Confirm',
      });
    } else {
      remove(index, value);
    }
  };

  const onUpdate = async (name: string, isDirty: boolean) => {
    if (isDirty) {
      try {
        setInProgress(true);
        await update(index, { ...value, name });
      } catch (e) {
        console.error(e);
        editableRef.current?.setIsEdit(true);
      } finally {
        setInProgress(false);
      }
    }
  };

  return (
    <div className={clsx('flex text-xs gap-1 items-center ml-2 justify-between', {
      'bg-gray-100': inProgress,
    })}
    >
      <Editable
        instanceRef={editableRef as RefObject<EditableInstance>}
        controlRef={roomNameControlRef as RefObject<HTMLInputElement>}
        idleView={(
          <strong className="font-semibold">
            {inProgress ? 'Saving...' : (
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: roomColor }} />
                <span className="text-xs">{name}</span>
              </div>
            )}
          </strong>
        )}
        readonly={inProgress}
        tooltip="Edit Room"
        showEdit={isNil(roomSettingId)}
        classes={{ editableRoot: '!text-black', idleRoot: '!flex items-center justify-between !w-full' }}
      >
        <Controller
          control={control}
          name={`rooms.${index}.name`}
          rules={{ validate: notEmptyString }}
          render={({ field, fieldState: { invalid, isDirty } }) => (
            <Input
              {...field}
              onBlur={() => {
                field.onBlur();
                if (notEmptyString(field.value)) {
                  editableRef.current?.setIsEdit?.(false);
                  onUpdate(field.value, isDirty);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && isDirty && notEmptyString(field.value)) {
                  onUpdate(field.value, isDirty);
                  editableRef.current?.setIsEdit?.(false);
                } else if (/^(enter|escape)$/i.test(e.key)) {
                  resetField('rooms');
                  editableRef.current?.setIsEdit?.(false);
                }
              }}
              id="room-name"
              className="flex-1"
              inputRef={roomNameControlRef}
              error={invalid}
              showErrorText={false}
              inputProps={{ maxLength: 64 }}
            />
          )}
        />
      </Editable>
      {(isEmptyString(currentName) || !isNil(roomSettingId)) && (
        <Tooltip title="Delete Room">
          <IconButton
            onClick={handleDeleteRoom}
            size="small"
            color="error"
            disabled={inProgress}
          >
            <DeleteIcon className="!w-5 !h-5 text-gray-500" />
          </IconButton>
        </Tooltip>
      )}
    </div>
  );
}
